import React, { useSyncExternalStore } from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import { isPreview } from '../../services/env';

const propTypes = {
  meta: PropTypes.object.isRequired,
  noIndex: PropTypes.bool,
};

// Inlined as data URIs so the icon can follow the OS theme without depending
// on an asset deployed by another app. The href swap (instead of a media
// query inside one SVG) is deliberate: Chrome rasterizes favicons once and
// ignores theme changes until the href changes.
const markSvg = (mark, notch) =>
  `data:image/svg+xml,${encodeURIComponent(
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -35.5 155 155">' +
      `<polygon fill="${mark}" points="19,0 155,0 136,84 0,84"/>` +
      `<polygon fill="${notch}" points="30,13 50,13 47,26.5 27,26.5"/>` +
      '</svg>',
  )}`;

const favicons = { light: markSvg('#0A0A0A', '#FFF'), dark: markSvg('#FFF', '#0A0A0A') };

const darkQuery = window.matchMedia ? window.matchMedia('(prefers-color-scheme: dark)') : null;

const subscribeToDarkQuery = (onChange) => {
  if (!darkQuery) return () => {};
  darkQuery.addEventListener('change', onChange);
  return () => darkQuery.removeEventListener('change', onChange);
};

const useFavicon = () => {
  const isDark = useSyncExternalStore(subscribeToDarkQuery, () =>
    Boolean(darkQuery && darkQuery.matches),
  );
  return favicons[isDark ? 'dark' : 'light'];
};

const Meta = ({ meta, noIndex = false, children }) => {
  const favicon = useFavicon();

  return (
    <Helmet>
      {meta.title ? <title>{meta.title} - Checkout Page</title> : ''}
      {meta.title ? <meta property="og:title" content={meta.title} /> : ''}
      {meta.title ? <meta name="twitter:title" content={meta.title} /> : ''}

      <link rel="icon" href="https://checkoutpage.com/img/favicon-32x32.png" key="favicon" />

      <link rel="icon" href={favicon} sizes="any" type="image/svg+xml" key="favicon-svg" />

      <link rel="apple-touch-icon" href="https://checkoutpage.com/img/apple-touch-icon.png" />

      <link rel="mask-icon" href="https://checkoutpage.com/img/logo.svg" color="black" />

      {meta.description ? <meta name="twitter:description" content={meta.description} /> : ''}
      {meta.description ? <meta property="og:description" content={meta.description} /> : ''}
      {meta.description ? <meta name="description" content={meta.description} /> : ''}

      {meta.url ? <meta property="og:url" content={meta.url} /> : ''}
      {meta.url ? <meta name="twitter:url" content={meta.url} /> : ''}

      {Object.prototype.hasOwnProperty.call(meta, 'image') ? (
        <meta property="og:image" content={meta.image} />
      ) : (
        ''
      )}

      {/*
      The react-helmet library has trouble with Fragments in development mode because:
      1. Fragments create internal Symbol properties for React's reconciliation
      2. Helmet tries to convert these to strings for processing
      3. JavaScript can't convert Symbols to strings directly
*/}
      {(noIndex || isPreview) && [
        <meta key="robots" name="robots" content="noindex" />,
        <meta key="googlebot" name="googlebot" content="noindex" />,
      ]}

      {children}
    </Helmet>
  );
};

Meta.propTypes = propTypes;
Meta.defaultProps = { noIndex: false };

export default Meta;
